/**
 * Admin Authentication Guard
 * Protects admin routes with comprehensive security checks
 */
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import authService from "../services/authService";
import { isTokenExpired, isValidTokenFormat, shouldRedirectToLogin, clearAllAuthData } from "../utils/tokenUtils";

export default function AdminAuthGuard({ children }) {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const checkAuthentication = () => {
      try {
        // CRITICAL: Start with false - assume not authorized until proven otherwise
        setIsAuthorized(false);
        
        const token = localStorage.getItem('adminAuthToken');
        
        // Check 1: Token exists - REQUIRED
        if (!token || token.trim() === '') {
          clearAllAuthData();
          setIsAuthorized(false);
          setIsChecking(false);
          return;
        }

        // Check 2: Token format is valid - REQUIRED
        if (!isValidTokenFormat(token)) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Invalid admin token format detected');
          }
          clearAllAuthData();
          setIsAuthorized(false);
          setIsChecking(false);
          return;
        }

        // Check 3: Token is not expired - REQUIRED
        if (isTokenExpired(token)) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Admin token expired');
          }
          // Clear all auth data - expired token means not authenticated
          clearAllAuthData();
          setIsAuthorized(false);
          setIsChecking(false);
          return;
        }

        // Check 4: Admin data exists - REQUIRED
        const adminData = localStorage.getItem('adminData');
        if (!adminData || adminData.trim() === '') {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Admin data missing');
          }
          clearAllAuthData();
          setIsAuthorized(false);
          setIsChecking(false);
          return;
        }

        // Check 5: Ensure user tokens are not present (prevent cross-authentication)
        const userToken = localStorage.getItem('authToken');
        if (userToken) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('User session detected while accessing admin routes - clearing user session');
          }
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userData');
        }

        // All checks passed - ONLY NOW set authorized to true
        setIsAuthorized(true);
        setIsChecking(false);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Admin authentication check error:', error);
        }
        // On ANY error, clear auth and deny access
        clearAllAuthData();
        setIsAuthorized(false);
        setIsChecking(false);
      }
    };

    // Run check immediately
    checkAuthentication();

    // Set up periodic token validation (every 5 minutes)
    const validationInterval = setInterval(() => {
      const token = localStorage.getItem('adminAuthToken');
      if (token && isTokenExpired(token)) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Admin token expired during session');
        }
        clearAllAuthData();
        setIsAuthorized(false);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(validationInterval);
  }, [location.pathname]);

  // Show loading state while checking
  if (isChecking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <div style={{ fontSize: '18px', marginBottom: '10px' }}>Verifying admin authentication...</div>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // CRITICAL: Not authenticated - redirect to admin login FIRST
  // This check MUST come before any other logic
  if (!isAuthorized) {
    // Clear any stale authentication data to prevent bypass
    clearAllAuthData();
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Double-check authentication before allowing any redirects
  // This prevents any edge cases where isAuthorized might be incorrectly set
  const finalTokenCheck = localStorage.getItem('adminAuthToken');
  const finalDataCheck = localStorage.getItem('adminData');
  
  if (!finalTokenCheck || !finalDataCheck || !isValidTokenFormat(finalTokenCheck) || isTokenExpired(finalTokenCheck)) {
    // Authentication failed on final check - clear and redirect to login
    clearAllAuthData();
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Only proceed with redirects if user is FULLY authenticated (passed all checks)
  // If logged in but hits /admin directly, redirect to dashboard
  if (location.pathname === "/admin" || location.pathname === "/admin/") {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  // All security checks passed - grant access
  return children;
}
